import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback
} from 'react';
import { createPortal } from 'react-dom';
import { TooltipTrigger, Tooltip, ActionButton } from '@adobe/react-spectrum';
import styles from './Field.module.scss';
import slideOutStyles from '../SlideOutPanel.module.scss';
import dropdownStyles from './Dropdown.module.scss';
import '../styles/menuDropdown.css';
import '../styles/menuDropdownStatus.css';
import '../styles/menuDropdownEvent.css';
import '../styles/menuDropdownParentMoment.css';
import '../styles/menuDropdownTier.css';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import { PARENT_MOMENT_TOOLTIP_MESSAGE } from 'utils/constants';

// Module-level state to track the currently open dropdown instance
let currentOpenDropdown: string | null = null;
const dropdownCloseCallbacks = new Map<string, () => void>();

interface DropdownOption {
  id: string;
  name: string;
}

interface EditableFieldProps {
  label: string;
  value: string;
  options: string[] | DropdownOption[];
  onChange: (value: string) => void;
  type?: 'status' | 'default' | 'event' | 'parentMoment' | 'tier';
  id?: string;
  isPhased?: boolean;
  disabled?: boolean; // RBAC: Disable editing when user doesn't have permission
}

// Removed custom CheckIcon - now using React Spectrum Checkmark for consistency

// Separate component for dropdown option to properly use hooks
interface DropdownOptionItemProps {
  option: string | DropdownOption;
  index: number;
  isSelected: boolean;
  getOptionName: (option: any) => string;
  tagClass: (optionName: string) => string;
  labelClass: string;
  checkIconClass: string;
  dropdownOptionClass: string;
  hoveredOptionIndex: number | null;
  setHoveredOptionIndex: (index: number | null) => void;
  isHighlighted?: boolean;
  onChange: (value: string) => void;
  closeDropdown: () => void;
  instanceId: string;
  id?: string;
  getStatusIcon: (optionName: string) => React.ReactNode;
}

const DropdownOptionItem: React.FC<DropdownOptionItemProps> = ({
  option,
  index,
  isSelected,
  getOptionName,
  tagClass,
  labelClass,
  checkIconClass,
  dropdownOptionClass,
  hoveredOptionIndex,
  setHoveredOptionIndex,
  onChange,
  closeDropdown,
  instanceId,
  id,
  getStatusIcon,
  isHighlighted
}) => {
  const optionName = getOptionName(option);
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const [hasTextOverflow, setHasTextOverflow] = React.useState(false);

  // Check if text is truncated on mount and when option changes
  React.useEffect(() => {
    if (labelRef.current) {
      const isOverflowing =
        labelRef.current.scrollWidth > labelRef.current.clientWidth;
      setHasTextOverflow(isOverflowing);
    }
  }, [optionName]);

  return (
    <div
      key={index}
      className={dropdownOptionClass}
      data-option-index={index}
      onClick={e => {
        e.stopPropagation();
        // Handle click on the entire row
        onChange(optionName);
        closeDropdown();
        setHoveredOptionIndex(null);
        if (currentOpenDropdown === instanceId) {
          currentOpenDropdown = null;
        }
      }}
      style={{
        cursor: 'pointer',
        ...(isHighlighted
          ? { backgroundColor: '#e9e9e9', borderRadius: '4px' }
          : {})
      }}
    >
      <div
        className={`${tagClass(optionName)} ${styles.dropdownOptionWrapper}`}
        style={{
          border: 'none',
          backgroundColor: 'transparent',
          outline: 'none',
          width: '100%',
          padding: 0,
          textAlign: 'left',
          justifyContent: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'Adobe Clean',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.43,
          color: '#000000'
        }}
      >
        {id === 'status' && getStatusIcon(optionName)}
        <span
          ref={labelRef}
          className={labelClass}
          title={hasTextOverflow ? optionName : ''}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            maxWidth: '280px',
            fontFamily: 'Adobe Clean',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.43,
            color: '#000000',
            flex: 1
          }}
        >
          {optionName}
        </span>
        {isSelected && (
          <span
            className={checkIconClass}
            style={{ flexShrink: 0, marginLeft: '8px' }}
          >
            <BlackCheckMarkIcon width={18} height={16} />
          </span>
        )}
      </div>
    </div>
  );
};

const getRiskLevelInfo = (option: string) => {
  if (!option.includes('Risk Level:')) return null;

  const level = option.toLowerCase().split('risk level: ')[1];
  switch (level) {
    case 'low':
      return { shape: 'circle', level };
    case 'moderate':
      return { shape: 'diamond', level };
    case 'high':
      return { shape: 'square', level };
    default:
      return null;
  }
};

const RiskIndicator = ({ option }: { option: string }) => {
  const riskInfo = getRiskLevelInfo(option);
  if (!riskInfo) return null;

  return (
    <div
      className={`${dropdownStyles[riskInfo.shape]} ${
        dropdownStyles[riskInfo.level]
      }`}
    />
  );
};

const getStatusIcon = (option: string) => {
  if (option === 'Risk Level: Low')
    return (
      <LegendRiskLowIcon style={{ width: 18, height: 18, marginRight: 4 }} />
    );
  if (option === 'Risk Level: Moderate')
    return (
      <LegendRiskModerateIcon
        style={{ width: 18, height: 18, marginRight: 4 }}
      />
    );
  if (option === 'Risk Level: High')
    return (
      <LegendRiskHighIcon style={{ width: 18, height: 18, marginRight: 4 }} />
    );
  return null;
};

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  options,
  onChange,
  type = 'default',
  id,
  isPhased = false,
  disabled = false
}) => {
  // Generate a unique ID for this component instance
  const instanceId = useRef(
    `editable-field-${Math.random().toString(36).substr(2, 9)}`
  ).current;

  // Helper function to check if options are objects with id/name
  const isOptionsArray = (
    opts: string[] | DropdownOption[]
  ): opts is DropdownOption[] => {
    return opts.length > 0 && typeof opts[0] === 'object' && 'id' in opts[0];
  };

  // Helper function to get option display name
  const getOptionName = (option: string | DropdownOption): string => {
    return typeof option === 'string' ? option : option.name;
  };

  // Helper function to get option ID (for comparison)
  const getOptionId = (option: string | DropdownOption): string => {
    return typeof option === 'string' ? option : option.id;
  };

  // Helper function to check if option should be selected
  const isOptionSelected = (option: string | DropdownOption): boolean => {
    if (isOptionsArray(options)) {
      // For object options, compare by ID
      const optionId = getOptionId(option);
      return optionId === value;
    } else {
      // For string options, compare by name (existing behavior)
      const optionName = getOptionName(option);
      return optionName === value;
    }
  };

  // Helper function to get display value (name) for the current selection
  const getDisplayValue = (): string => {
    if (isOptionsArray(options)) {
      // For object options, find the option by ID and return its name
      const selectedOption = options.find(opt => getOptionId(opt) === value);
      return selectedOption ? getOptionName(selectedOption) : value;
    } else {
      // For string options, value is already the display name
      return value;
    }
  };
  const [isOpen, setIsOpen] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rafRef = useRef<number | null>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const optionsListRef = useRef<HTMLDivElement>(null);

  // Function to close this dropdown
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    // Clean up animation frame when closing
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    setForceUpdate(0);
  }, []);

  // Remove wrapping for dropdown values - always show ellipsis with tooltip instead
  // const isWrappingField = id === 'event' || id === 'parentMoment';

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    return (options as Array<string | DropdownOption>).filter(
      (option: string | DropdownOption) => {
        const optionName = getOptionName(option);
        return optionName.toLowerCase().includes(searchText.toLowerCase());
      }
    ) as typeof options;
  }, [options, searchText]);

  // Clear search text when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions]);

  // Register close callback and handle opening/closing
  useEffect(() => {
    // Register this instance's close callback
    dropdownCloseCallbacks.set(instanceId, closeDropdown);

    return () => {
      // Clean up on unmount
      dropdownCloseCallbacks.delete(instanceId);
      if (currentOpenDropdown === instanceId) {
        currentOpenDropdown = null;
      }
    };
  }, [instanceId, closeDropdown]);

  // Function to open this dropdown and close others
  const openDropdown = useCallback(() => {
    // Close any currently open dropdown (except this one)
    if (currentOpenDropdown && currentOpenDropdown !== instanceId) {
      const closeCallback = dropdownCloseCallbacks.get(currentOpenDropdown);
      if (closeCallback) {
        closeCallback();
      }
    }
    // Set this as the current open dropdown
    currentOpenDropdown = instanceId;
    setIsOpen(true);
  }, [instanceId]);

  // Calculate dropdown position on every render when open (like working status dropdown)
  const getDropdownPosition = () => {
    if (!buttonRef.current) {
      return { top: 0, left: 0, minWidth: 'auto' };
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    return {
      top: buttonRect.bottom + 8,
      left: buttonRect.left,
      minWidth: Math.max(buttonRect.width, 320) // Minimum 320px width for dropdown
    };
  };

  // Calculate position on every render when open, including when forceUpdate changes
  const dropdownPosition = isOpen
    ? (() => {
        // Reference forceUpdate to ensure re-calculation on scroll
        void forceUpdate;
        return getDropdownPosition();
      })()
    : { top: 0, left: 0, minWidth: 'auto' };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // For portal-based dropdown, check if click is outside both button and dropdown
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        // Check if click is inside this specific instance's portal dropdown
        if (
          !portalDropdownRef.current ||
          !portalDropdownRef.current.contains(event.target as Node)
        ) {
          closeDropdown();
          if (currentOpenDropdown === instanceId) {
            currentOpenDropdown = null;
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, instanceId, closeDropdown]);

  // Force re-render when scrolling to update dropdown position with smooth throttling
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => {
        // Cancel any pending animation frame
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        // Use requestAnimationFrame to throttle updates to display refresh rate (60fps)
        rafRef.current = requestAnimationFrame(() => {
          setForceUpdate(prev => prev + 1);
        });
      };

      // Listen to all scroll events using capture mode with passive for better performance
      window.addEventListener('scroll', handleScroll, {
        passive: true,
        capture: true
      });
      window.addEventListener('resize', handleScroll, { passive: true });

      return () => {
        // Clean up animation frame
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isOpen]);

  // Determine dropdown style by id or type
  let dropdownClass = 'menu-dropdown';
  let dropdownOptionClass = 'menu-dropdown-row';
  let tagClass = (option: string) => 'menu-dropdown-tag';
  let labelClass = 'menu-dropdown-tag-label';
  let checkIconClass = 'menu-dropdown-check-icon';

  if (id === 'status' || type === 'status') {
    dropdownClass = 'menu-dropdown-status';
    dropdownOptionClass = 'menu-dropdown-status-row';
    tagClass = (option: string) => {
      if (option === 'Risk Level: Low')
        return 'menu-dropdown-status-tag variant-low';
      if (option === 'Risk Level: Moderate')
        return 'menu-dropdown-status-tag variant-moderate';
      if (option === 'Risk Level: High')
        return 'menu-dropdown-status-tag variant-default';
      return 'menu-dropdown-status-tag';
    };
    labelClass = 'menu-dropdown-status-tag-label';
    checkIconClass = 'menu-dropdown-status-check-icon';
  } else if (id === 'event' || type === 'event') {
    dropdownClass = 'menu-dropdown-event';
    dropdownOptionClass = 'menu-dropdown-event-row';
    tagClass = () => 'menu-dropdown-event-tag';
    labelClass = 'menu-dropdown-event-tag-label';
    checkIconClass = 'menu-dropdown-event-check-icon';
  } else if (id === 'parentMoment' || type === 'parentMoment') {
    dropdownClass = 'menu-dropdown-parent-moment';
    dropdownOptionClass = 'menu-dropdown-parent-moment-row';
    tagClass = () => 'menu-dropdown-parent-moment-tag';
    labelClass = 'menu-dropdown-parent-moment-tag-label';
    checkIconClass = 'menu-dropdown-parent-moment-check-icon';
  } else if (id === 'tier' || type === 'tier') {
    dropdownClass = 'menu-dropdown-tier';
    dropdownOptionClass = 'menu-dropdown-tier-row';
    tagClass = () => 'menu-dropdown-tier-tag';
    labelClass = 'menu-dropdown-tier-tag-label';
    checkIconClass = 'menu-dropdown-tier-check-icon';
  }

  const renderRiskIndicator = (option: string) => {
    if (type !== 'status') return null;

    const riskInfo = getRiskLevelInfo(option);
    if (!riskInfo) return null;

    return (
      <div
        className={`${dropdownStyles.riskIndicator} ${
          dropdownStyles[riskInfo.shape]
        } ${dropdownStyles[riskInfo.level]}`}
      />
    );
  };

  // State and ref to check for text overflow (ellipsis)
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  // Check if text is overflowing (has ellipsis)
  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setHasOverflow(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [value, getDisplayValue()]);

  return (
    <div
      className={`${styles.fieldContainer} ${
        isPhased ? styles.phasedLayout : ''
      }`}
    >
      <ToolTipWrapper
        message={label === 'PARENT MOMENT' ? PARENT_MOMENT_TOOLTIP_MESSAGE : ''}
      >
        <div className={styles.label}>{label}</div>
      </ToolTipWrapper>
      <div
        className={`${styles.valueContainer} ${slideOutStyles.valueColumnFixed} ${slideOutStyles.dropdownField}`}
        ref={dropdownRef}
      >
        {/* Only show tooltip if text has overflow (ellipsis) */}
        {hasOverflow ? (
          <TooltipTrigger delay={300}>
            <div className={styles.valueWrapper} ref={buttonRef}>
              <ActionButton
                isQuiet
                isDisabled={disabled}
                UNSAFE_className={styles.value}
                onPress={() => {
                  if (disabled) return; // RBAC: Prevent opening dropdown if disabled
                  if (isOpen) {
                    closeDropdown();
                    if (currentOpenDropdown === instanceId) {
                      currentOpenDropdown = null;
                    }
                  } else {
                    openDropdown();
                  }
                }}
                UNSAFE_style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '120px',
                  minHeight: '20px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  outline: 'none',
                  padding: 0,
                  fontFamily: 'Adobe Clean',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: 1.43,
                  color: '#000000',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}
              >
                {type === 'status' && (
                  <RiskIndicator option={getDisplayValue()} />
                )}
                <span
                  ref={textRef}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    fontFamily: 'Adobe Clean',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 1.43,
                    color: '#000000',
                    textAlign: 'left'
                  }}
                >
                  {getDisplayValue()}
                </span>
              </ActionButton>
            </div>
            <Tooltip
              UNSAFE_style={{
                fontFamily: 'Adobe Clean',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 1.43
              }}
            >
              {getDisplayValue()}
            </Tooltip>
          </TooltipTrigger>
        ) : (
          // No tooltip when text doesn't overflow
          <div className={styles.valueWrapper} ref={buttonRef}>
            <ActionButton
              isQuiet
              isDisabled={disabled}
              UNSAFE_className={styles.value}
              onPress={() => {
                if (disabled) return;
                if (isOpen) {
                  closeDropdown();
                  if (currentOpenDropdown === instanceId) {
                    currentOpenDropdown = null;
                  }
                } else {
                  openDropdown();
                }
              }}
              UNSAFE_style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '120px',
                minHeight: '20px',
                border: 'none',
                backgroundColor: 'transparent',
                outline: 'none',
                padding: 0,
                fontFamily: 'Adobe Clean',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 1.43,
                color: '#000000',
                textAlign: 'left',
                justifyContent: 'flex-start'
              }}
            >
              {type === 'status' && (
                <RiskIndicator option={getDisplayValue()} />
              )}
              <span
                ref={textRef}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'Adobe Clean',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: 1.43,
                  color: '#000000',
                  textAlign: 'left',
                  position: 'relative'
                }}
              >
                {getDisplayValue()}
              </span>
            </ActionButton>
          </div>
        )}
        {isOpen && (
          <div
            ref={portalDropdownRef}
            style={{
              position: 'absolute',
              top: '100%',
              zIndex: 100
            }}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <div
              ref={optionsListRef}
              className={dropdownClass}
              style={{
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            >
              {/* Search Field */}
              <div
                className="menu-dropdown-search-container"
                onClick={e => e.stopPropagation()}
                style={{
                  padding: '8px',
                  position: 'sticky',
                  top: '-5px',
                  background: '#ffffff',
                  zIndex: 10,
                  marginTop: '-5px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '32px',
                    background: '#ffffff',
                    border: '2px solid #131313',
                    borderRadius: '16px',
                    paddingLeft: '14px',
                    gap: '6px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px 0',
                      flexShrink: 0
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18.5303 17.4697L13.447 12.3865C14.4164 11.1851 15 9.66003 15 8C15 4.14062 11.8594 1 8 1C4.14062 1 1 4.14061 1 7.99999C1 11.8594 4.14062 15 8 15C9.66003 15 11.1851 14.4164 12.3865 13.447L17.4697 18.5303C17.6162 18.6767 17.8076 18.75 18 18.75C18.1924 18.75 18.3838 18.6768 18.5303 18.5303C18.8232 18.2373 18.8232 17.7627 18.5303 17.4697ZM8 13.5C4.96777 13.5 2.5 11.0322 2.5 7.99999C2.5 4.96776 4.96777 2.49999 8 2.49999C11.0322 2.49999 13.5 4.96776 13.5 7.99999C13.5 11.0322 11.0322 13.5 8 13.5Z"
                        fill="#717171"
                      />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={`Search ${label.toLowerCase()}`}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setHighlightedIndex(prev => {
                          const next = Math.min(
                            prev + 1,
                            filteredOptions.length - 1
                          );
                          requestAnimationFrame(() => {
                            optionsListRef.current
                              ?.querySelector(`[data-option-index="${next}"]`)
                              ?.scrollIntoView({ block: 'nearest' });
                          });
                          return next;
                        });
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setHighlightedIndex(prev => {
                          const next = Math.max(prev - 1, 0);
                          requestAnimationFrame(() => {
                            optionsListRef.current
                              ?.querySelector(`[data-option-index="${next}"]`)
                              ?.scrollIntoView({ block: 'nearest' });
                          });
                          return next;
                        });
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (
                          highlightedIndex >= 0 &&
                          highlightedIndex < filteredOptions.length
                        ) {
                          const option = filteredOptions[highlightedIndex];
                          onChange(getOptionName(option));
                          closeDropdown();
                          setHighlightedIndex(-1);
                          if (currentOpenDropdown === instanceId) {
                            currentOpenDropdown = null;
                          }
                        }
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        closeDropdown();
                        setHighlightedIndex(-1);
                        if (currentOpenDropdown === instanceId) {
                          currentOpenDropdown = null;
                        }
                      }
                    }}
                    autoFocus
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontFamily:
                        'Adobe Clean, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '1.2857142857142858em',
                      color: '#131313',
                      padding: '7px 0'
                    }}
                  />
                  {searchText && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setSearchText('');
                        searchInputRef.current?.focus();
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        padding: '6px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        flexShrink: 0,
                        marginRight: '6px'
                      }}
                      onMouseEnter={e => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.background = '#f5f5f5';
                      }}
                      onMouseLeave={e => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.background = 'transparent';
                      }}
                      onMouseDown={e => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.background = '#e5e5e5';
                      }}
                      onMouseUp={e => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.background = '#f5f5f5';
                      }}
                      aria-label="Clear search"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="8"
                        height="8"
                        viewBox="0 0 8 8"
                        fill="none"
                      >
                        <path
                          d="M5.23779 4L7.69384 1.54395C8.03565 1.20215 8.03565 0.647469 7.69384 0.305669C7.35204 -0.0361311 6.79834 -0.0361311 6.45654 0.305669L4 2.76221L1.54346 0.305659C1.20166 -0.0361311 0.647955 -0.0361311 0.306155 0.305659C-0.0356453 0.647449 -0.0356353 1.20215 0.306155 1.54395L2.7622 4L0.306155 6.45605C-0.0356453 6.79785 -0.0356453 7.35253 0.306155 7.69433C0.477055 7.86523 0.700685 7.95019 0.924805 7.95019C1.14892 7.95019 1.37255 7.86523 1.54345 7.69433L3.99999 5.23779L6.45653 7.69433C6.62743 7.86523 6.85106 7.95019 7.07518 7.95019C7.2993 7.95019 7.52293 7.86523 7.69383 7.69433C8.03563 7.35253 8.03563 6.79785 7.69383 6.45605L5.23779 4Z"
                          fill="#292929"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Options List */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <DropdownOptionItem
                    key={index}
                    option={option}
                    index={index}
                    isSelected={isOptionSelected(option)}
                    isHighlighted={highlightedIndex === index}
                    getOptionName={getOptionName}
                    tagClass={tagClass}
                    labelClass={labelClass}
                    checkIconClass={checkIconClass}
                    dropdownOptionClass={dropdownOptionClass}
                    hoveredOptionIndex={hoveredOptionIndex}
                    setHoveredOptionIndex={setHoveredOptionIndex}
                    onChange={onChange}
                    closeDropdown={closeDropdown}
                    instanceId={instanceId}
                    id={id}
                    getStatusIcon={getStatusIcon}
                  />
                ))
              ) : (
                <div
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    color: '#6d6d6d',
                    fontSize: '14px',
                    fontFamily: 'Adobe Clean'
                  }}
                >
                  No results found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableField;
